import type {
  DemoAppointment,
  DemoAppointmentPatientSex,
  EncounterDiagnosisSource,
  EncounterPatientProfile,
  EncounterPatientSex,
  EncounterSnapshot,
  EncounterSoapNote,
  ProblemSynthesis,
  SuggestedDiagnosisCode,
} from "@emr/contracts";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

export interface DemoExecutedActionRecord {
  readonly actionKey: string;
  readonly kind: string;
  readonly title: string;
  readonly executedAt: string;
  /**
   * Exact source plan-action wording used to restore executed action rows on
   * resume. Older receipts can omit these fields; rows with insufficient
   * wording are omitted rather than describing a different artifact.
   */
  readonly detail?: string;
  readonly rationale?: string;
  readonly priority?: "routine" | "soon" | "urgent";
}

export interface DemoFollowUpDocumentationRecord {
  readonly id: string;
  readonly branchLabel: string;
  readonly followUpId: string;
  readonly kind: "ask" | "examine";
  readonly prompt: string;
  readonly noteSection: "subjective" | "objective";
  readonly reviewedResponse: string;
  readonly source: "typed" | "recorded";
  readonly sourceExcerpt?: string;
  readonly suggestedChartText?: string;
  readonly chartText: string;
  readonly insertedAt: string;
}

export interface DemoFollowUpNoteAdditions {
  readonly subjective?: readonly string[];
  readonly objective?: readonly string[];
}

export type DemoFollowUpAppendResult =
  | { readonly status: "accepted" }
  | {
      readonly status: "rejected";
      readonly reason: "encounter_not_found" | "encounter_locked" | "empty_batch";
    }
  | {
      readonly status: "rejected";
      readonly reason: "section_full";
      readonly section: "subjective" | "objective";
    };

/** A synthetic lab result tracked locally; values derive deterministically from the id. */
export interface DemoLabResultRecord {
  readonly id: string;
  readonly patientId: string;
  readonly encounterId: string;
  readonly actionKey: string;
  readonly testName: string;
  readonly orderedAt: string;
  readonly availableAt: string;
  readonly acknowledgedAt?: string | null;
  readonly acknowledgedBy?: string | null;
}

export interface DemoEncounterProblemDiagnosis {
  readonly problemId: string;
  readonly code: SuggestedDiagnosisCode;
  readonly source: EncounterDiagnosisSource;
}

export interface DemoEncounterProblemState {
  readonly encounterProblems?: readonly { readonly id: string; readonly label: string }[];
  readonly problemSelections?: readonly {
    readonly problemId: string;
    readonly diagnosisLabel: string;
    readonly confidence: "high" | "moderate" | "low";
  }[];
  readonly problemDiagnoses?: readonly DemoEncounterProblemDiagnosis[];
}

export interface DemoEncounterRecord extends DemoEncounterProblemState {
  readonly id: string;
  readonly patientId: string;
  readonly chiefComplaint: string;
  readonly startedAt: string;
  readonly updatedAt: string;
  readonly draftSavedAt: string | null;
  readonly schedulingAppointmentId?: string;
  readonly note: EncounterSoapNote;
  /** Manual A/P instructions; generated note writes must not change this provenance. */
  readonly clinicianIntent?: { readonly assessment: string; readonly plan: string };
  /**
   * Exact source Q&A and clinician-final chart wording are retained as local
   * provenance, but intentionally excluded from the reasoning snapshot.
   */
  readonly followUpDocumentation?: readonly DemoFollowUpDocumentationRecord[] | undefined;
  readonly diagnosisCode?: SuggestedDiagnosisCode | undefined;
  /**
   * Who put the documented code on the chip: the applied synthesis
   * (`synthesis_suggested`) or the physician's own edit (`clinician_selected`).
   * Travels with the code to the persisted encounter diagnoses. Present on
   * every record this provider writes with a code (the writers require it and
   * stored records are normalised on load — see `UNDECLARED_DIAGNOSIS_SOURCE`);
   * read it through `documentedDiagnosisOf`, which never invents a provenance.
   */
  readonly diagnosisSource?: EncounterDiagnosisSource | undefined;
  /** Synthetic execution record — local only, nothing is transmitted. */
  readonly executedActions?: readonly DemoExecutedActionRecord[] | undefined;
  /** What Discard drafts puts back, kept here so a reload does not lose it. Local only. */
  readonly discardBaseline?: DemoDiscardBaseline | undefined;
  readonly completedAt?: string | null;
  readonly completedBy?: string | null;
}

/** The note and code before the plan was applied, and the problem blocks the plan wrote. */
export interface DemoDiscardBaseline {
  readonly note?: { readonly assessment: string; readonly plan: string };
  readonly diagnosis?: DemoDocumentedDiagnosis | null;
  readonly problems?: readonly ProblemSynthesis[];
}

/** A note hydrated without provenance is conservatively clinician-authored. */
export function encounterClinicianIntent(
  encounter: Pick<DemoEncounterRecord, "note" | "clinicianIntent">,
) {
  return (
    encounter.clinicianIntent ?? {
      assessment: encounter.note.assessment,
      plan: encounter.note.plan,
    }
  );
}

export interface DemoPatientRecord extends EncounterPatientProfile {
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface DemoPatientInput {
  readonly displayName: string;
  readonly dob: string;
  readonly sex: EncounterPatientSex;
  readonly mrn?: string;
  readonly healthNumber: string;
  readonly healthVersionCode: string;
  readonly phone: string;
  readonly address: string;
  readonly allergies: readonly string[];
  readonly medications: readonly string[];
  readonly problems: readonly string[];
}

/**
 * Hydration input for an encounter that exists outside this workspace — in
 * the persisted backend or a synthetic fixture. Ids are the exact source ids
 * so the encounter workspace and the source stay aligned.
 */
export interface DemoExternalEncounterBaseInput extends DemoEncounterProblemState {
  readonly patientId: string;
  readonly encounterId: string;
  readonly displayName: string;
  readonly dob: string;
  readonly sex?: EncounterPatientSex;
  readonly mrn?: string;
  readonly phone?: string;
  readonly address?: string;
  /**
   * The persisted chart's health number — the provincial/federal coverage
   * member ID the workspace projects as healthCardNumber. Keeps the demo
   * documents (lab requisition, context panels) on the same identifier the
   * prescription pad and referral package print.
   */
  readonly healthNumber?: string;
  readonly chiefComplaint: string;
  readonly allergies?: readonly string[];
  readonly medications?: readonly string[];
  readonly problems?: readonly string[];
  readonly note?: EncounterSoapNote;
  readonly completedAt?: string | null;
  readonly completedBy?: string | null;
}

/**
 * The persisted encounter's documented diagnosis code (the rank-1 ICD-10-CM
 * row of its saved encounter diagnoses) together with the row's provenance,
 * or null when it holds none; omitted when hydration says nothing about it.
 * Adopted under the same never-edited-locally rule as the note: a chip the
 * physician touched in this browser always wins over re-hydration.
 */
export type DemoExternalEncounterDiagnosis =
  | {
      readonly diagnosisCode: SuggestedDiagnosisCode;
      readonly diagnosisSource: EncounterDiagnosisSource;
    }
  | { readonly diagnosisCode: null; readonly diagnosisSource?: undefined }
  | { readonly diagnosisCode?: undefined; readonly diagnosisSource?: undefined };

export type DemoExternalEncounterInput = DemoExternalEncounterBaseInput &
  DemoExternalEncounterDiagnosis;

interface DemoFollowUpAppendOutcome {
  readonly operationId: string;
  readonly result: DemoFollowUpAppendResult;
}

const FOLLOW_UP_APPEND_OUTCOMES = Symbol("demo-follow-up-append-outcomes");

export interface DemoWorkspaceState {
  readonly patients: DemoPatientRecord[];
  readonly encounters: DemoEncounterRecord[];
  readonly results: DemoLabResultRecord[];
  /**
   * Commit outcomes are kept in React state so callers only observe a result
   * after the atomic workspace update commits. Symbol keys are intentionally
   * omitted from the localStorage JSON snapshot.
   */
  readonly [FOLLOW_UP_APPEND_OUTCOMES]?: readonly DemoFollowUpAppendOutcome[];
}

/**
 * The documented code travels with its provenance: a non-null `diagnosisCode`
 * must say who put it there, on every surface. There is no default — the
 * record may claim `clinician_selected` only where the physician's own edit
 * path says so; a code the synthesis applied is `synthesis_suggested` until
 * the physician edits it. `null` clears the code and its provenance together.
 */
export type DemoEncounterDiagnosisPatch =
  | {
      readonly diagnosisCode: SuggestedDiagnosisCode;
      readonly diagnosisSource: EncounterDiagnosisSource;
    }
  | { readonly diagnosisCode: null; readonly diagnosisSource?: undefined }
  | { readonly diagnosisCode?: undefined; readonly diagnosisSource?: undefined };

export type DemoEncounterWorkflowPatch = {
  readonly clinicianIntent?: { readonly assessment: string; readonly plan: string };
  readonly executedActions?: readonly DemoExecutedActionRecord[] | null;
  readonly discardBaseline?: DemoDiscardBaseline | null;
  readonly completedAt?: string | null;
  readonly completedBy?: string | null;
} & DemoEncounterDiagnosisPatch &
  DemoEncounterProblemState;

/** The documented code as the record holds it: the chip and who put it there. */
export interface DemoDocumentedDiagnosis {
  readonly code: SuggestedDiagnosisCode;
  readonly source: EncounterDiagnosisSource;
}

interface DemoWorkspaceContextValue extends DemoWorkspaceState {
  addPatient(input: DemoPatientInput): DemoPatientRecord;
  updatePatient(patientId: string, input: DemoPatientInput): DemoPatientRecord;
  startEncounter(patientId: string, chiefComplaint: string): DemoEncounterRecord;
  updateEncounterNote(
    encounterId: string,
    note: EncounterSoapNote | ((current: EncounterSoapNote) => EncounterSoapNote),
    source?: "clinician",
  ): void;
  appendEncounterFollowUps(
    encounterId: string,
    additions: DemoFollowUpNoteAdditions,
    records: readonly DemoFollowUpDocumentationRecord[],
  ): Promise<DemoFollowUpAppendResult>;
  updateEncounterWorkflow(encounterId: string, patch: DemoEncounterWorkflowPatch): void;
  recordLabResults(records: readonly DemoLabResultRecord[]): void;
  removeEncounterResults(encounterId: string): void;
  acknowledgeLabResult(resultId: string, acknowledgedBy: string): void;
  saveEncounterDraft(encounterId: string): EncounterSnapshot;
  patientFor(patientId: string): DemoPatientRecord | undefined;
  encounterFor(encounterId: string): DemoEncounterRecord | undefined;
  snapshotFor(encounterId: string): EncounterSnapshot | undefined;
  ensureScheduledEncounter(appointment: DemoAppointment): {
    readonly patient: DemoPatientRecord;
    readonly encounter: DemoEncounterRecord;
  };
  /**
   * Idempotently upserts a patient and open encounter using the exact
   * caller-supplied persisted ids. Existing local records are never discarded:
   * locally edited notes win over hydration, and repeated identical calls
   * leave state untouched.
   */
  ensureExternalEncounter(input: DemoExternalEncounterInput): {
    readonly patient: DemoPatientRecord;
    readonly encounter: DemoEncounterRecord;
  };
  /**
   * Clears the in-memory workspace and any cached workspace when a persisted
   * session ends. The server retains the clinical record.
   */
  clearLocalWorkspace(): void;
}

const STORAGE_KEY = "forma.synthetic-demo-workspace.v1";

/**
 * Keeps the newer copy of each record, by `updatedAt`. Returns `mine` itself when nothing in
 * `theirs` is newer, so an unchanged merge does not re-render or re-write.
 */
function newerRecords<T extends { readonly id: string; readonly updatedAt: string }>(
  mine: T[],
  theirs: readonly T[],
): T[] {
  const incoming = new Map(theirs.map((record) => [record.id, record]));
  let changed = false;
  const merged = mine.map((record) => {
    const other = incoming.get(record.id);
    incoming.delete(record.id);
    if (other === undefined || other.updatedAt <= record.updatedAt) return record;
    changed = true;
    return other;
  });
  if (incoming.size > 0) changed = true;
  return changed ? [...merged, ...incoming.values()] : mine;
}

/**
 * Two app tabs share this store and each writes all of it, so a tab opened earlier would put
 * back its older note on its next write. Merging the other tab's write keeps the newer copy of
 * every patient and encounter; this tab's own newer copies are then written back. Lab results stay
 * this tab's own: they are removed on purpose (an undone order), which a merge would undo.
 */
export function mergeWorkspaceStates(
  mine: DemoWorkspaceState,
  theirs: Pick<DemoWorkspaceState, "patients" | "encounters">,
): DemoWorkspaceState {
  const patients = newerRecords(mine.patients, theirs.patients);
  const encounters = newerRecords(mine.encounters, theirs.encounters);
  return patients === mine.patients && encounters === mine.encounters
    ? mine
    : { ...mine, patients, encounters };
}
const DemoWorkspaceContext = createContext<DemoWorkspaceContextValue | null>(null);

/**
 * What a code of undeclared origin is filed as. The types make every writer
 * name the provenance; this is the runtime net for a caller that slipped past
 * them and for records stored before it was tracked (either surface's
 * synthesis apply or the physician's edit could have written those). Unknown
 * is never promoted to clinician review: an unreviewed claim can only be
 * understated, never a physician's attestation the physician never made. The
 * physician's own edit path is the one place `clinician_selected` comes from.
 */
export const UNDECLARED_DIAGNOSIS_SOURCE: EncounterDiagnosisSource = "synthesis_suggested";

/**
 * The documented code with its provenance, or null when the record holds no
 * code. Never invents a provenance: a code the record somehow holds without
 * one is not documented (writers and the load-time normalisation keep that
 * unreachable). The persisted diagnoses writer reads the chip through this.
 */
export function documentedDiagnosisOf(
  encounter: DemoEncounterRecord | undefined,
): DemoDocumentedDiagnosis | null {
  if (encounter?.diagnosisCode === undefined || encounter.diagnosisSource === undefined) {
    return null;
  }
  return { code: encounter.diagnosisCode, source: encounter.diagnosisSource };
}

function sameDiagnosisCode(
  left: SuggestedDiagnosisCode | null | undefined,
  right: SuggestedDiagnosisCode | null | undefined,
): boolean {
  if (!left || !right) return !left && !right;
  return (
    left.code === right.code &&
    left.description === right.description &&
    left.system === right.system
  );
}

function splitSeedName(name: string): string {
  return name
    .split(/\s+/)
    .map((part) => part[0] ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function now(): string {
  return new Date().toISOString();
}

function appendClinicalProse(existing: string, additions: readonly string[]): string {
  if (additions.length === 0) return existing;
  const joined = additions.join(" ");
  if (!existing) return joined;
  return `${existing}${/\s$/.test(existing) ? "" : " "}${joined}`;
}

function newId(prefix: string): string {
  const suffix =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${prefix}-${suffix}`;
}

function seedState(): DemoWorkspaceState {
  const seededAt = "2026-07-23T13:00:00.000Z";
  const patients: DemoPatientRecord[] = [
    {
      id: "demo-patient-avery",
      displayName: "Avery Morgan",
      dob: "1991-04-12",
      sex: "male",
      mrn: "DEMO-1001",
      healthNumber: "1234567890",
      healthVersionCode: "AB",
      phone: "416-555-0101",
      address: "100 Demo Street, Toronto, ON M5V 2T6",
      allergies: ["Penicillin — hives"],
      medications: ["Salbutamol inhaler as needed"],
      problems: ["Mild intermittent asthma", "Seasonal allergic rhinitis"],
      createdAt: seededAt,
      updatedAt: seededAt,
    },
    {
      id: "demo-patient-samira",
      displayName: "Samira Patel",
      dob: "1983-11-08",
      sex: "female",
      mrn: "DEMO-1002",
      healthNumber: "9876543210",
      healthVersionCode: "CD",
      phone: "416-555-0102",
      address: "200 Example Avenue, Toronto, ON M4B 1B3",
      allergies: [],
      medications: ["Ramipril 5 mg daily"],
      problems: ["Hypertension"],
      createdAt: seededAt,
      updatedAt: seededAt,
    },
  ];
  const encounters: DemoEncounterRecord[] = [
    {
      id: "demo-encounter-avery",
      patientId: patients[0]?.id ?? "demo-patient-avery",
      chiefComplaint: "Cough and sore throat",
      startedAt: seededAt,
      updatedAt: seededAt,
      draftSavedAt: null,
      note: {
        subjective:
          "Three days of dry cough, sore throat, and nasal congestion. Drinking fluids. No chest pain or shortness of breath.",
        objective:
          "Afebrile. SpO2 98% on room air. Mild pharyngeal erythema without exudate. Lungs clear bilaterally.",
        assessment: "",
        plan: "",
      },
    },
  ];
  return { patients, encounters, results: [] };
}

function loadInitialState(): DemoWorkspaceState {
  if (typeof window === "undefined") return seedState();
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (!stored) return seedState();
  try {
    const parsed = JSON.parse(stored) as Partial<DemoWorkspaceState>;
    if (Array.isArray(parsed.patients) && Array.isArray(parsed.encounters)) {
      return {
        patients: (parsed.patients as DemoPatientRecord[]).map(migrateStoredPatient),
        encounters: (parsed.encounters as DemoEncounterRecord[]).map(migrateStoredEncounter),
        results: Array.isArray(parsed.results) ? (parsed.results as DemoLabResultRecord[]) : [],
      };
    }
  } catch {
    window.localStorage.removeItem(STORAGE_KEY);
  }
  return seedState();
}

function migrateStoredPatient(patient: DemoPatientRecord): DemoPatientRecord {
  const legacy = patient as DemoPatientRecord & {
    readonly pronouns?: string;
    readonly sex?: EncounterPatientSex;
  };
  const { pronouns: legacyPronouns, ...withoutPronouns } = legacy;
  const sex =
    legacy.sex === "male" || legacy.sex === "female"
      ? legacy.sex
      : legacyPronouns === "he/him"
        ? "male"
        : "female";
  return {
    ...withoutPronouns,
    sex,
    healthNumber: legacy.healthNumber ?? "",
    healthVersionCode: legacy.healthVersionCode ?? "",
    phone: legacy.phone ?? "416-555-0100",
    address: legacy.address ?? "Synthetic address not yet recorded",
  };
}

/**
 * The synthetic chart records only male or female (EncounterPatientSex). A booking
 * whose sex is "other" or "unknown" gets the default ensureExternalEncounter
 * gives a persisted chart with no displayed sex; the appointment itself keeps
 * the patient's answer.
 */
function demoChartSexForBooking(sex: DemoAppointmentPatientSex): EncounterPatientSex {
  return sex === "male" ? "male" : "female";
}

/**
 * A stored code without a provenance predates its tracking: it is filed as
 * undeclared (never as clinician-selected) so the invariant "a code always
 * carries who put it there" holds for every record in state.
 */
function migrateStoredEncounter(encounter: DemoEncounterRecord): DemoEncounterRecord {
  if (encounter.diagnosisCode === undefined || encounter.diagnosisSource !== undefined) {
    return encounter;
  }
  return { ...encounter, diagnosisSource: UNDECLARED_DIAGNOSIS_SOURCE };
}

function normalizedList(values: readonly string[]): string[] {
  return [...new Set(values.map((value) => value.trim()).filter(Boolean))];
}

function normalizedPatient(
  input: DemoPatientInput,
  existing?: DemoPatientRecord,
): DemoPatientRecord {
  const changedAt = now();
  const displayName = input.displayName.trim();
  return {
    id: existing?.id ?? newId("demo-patient"),
    displayName,
    dob: input.dob,
    sex: input.sex,
    mrn: input.mrn?.trim() || existing?.mrn || `DEMO-${Date.now().toString().slice(-6)}`,
    healthNumber: input.healthNumber.trim(),
    healthVersionCode: input.healthVersionCode.trim().toUpperCase(),
    phone: input.phone.trim(),
    address: input.address.trim(),
    allergies: normalizedList(input.allergies),
    medications: normalizedList(input.medications),
    problems: normalizedList(input.problems),
    createdAt: existing?.createdAt ?? changedAt,
    updatedAt: changedAt,
  };
}

export function DemoWorkspaceProvider({
  children,
  persist = true,
  initialState,
}: {
  readonly children: ReactNode;
  /** Signed-visit viewers use isolated memory and must not overwrite live drafts. */
  readonly persist?: boolean;
  readonly initialState?: DemoWorkspaceState;
}) {
  const [state, setState] = useState<DemoWorkspaceState>(
    () =>
      initialState ??
      (persist ? loadInitialState() : { patients: [], encounters: [], results: [] }),
  );
  const followUpAppendResolversRef = useRef(
    new Map<string, (result: DemoFollowUpAppendResult) => void>(),
  );

  useEffect(() => {
    if (!persist) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      /* Full or unavailable browser storage: the copy in memory (and the server) still holds it. */
    }
  }, [persist, state]);

  // Another app tab wrote the store: keep the newer copy of each record.
  useEffect(() => {
    if (!persist) return;
    const onStorage = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY) return;
      // Another app tab ended the session and cleared the store: so does this one, or its next
      // write would put the previous session's records back for the next person here.
      if (event.newValue === null) {
        setState({ patients: [], encounters: [], results: [] });
        return;
      }
      let parsed: Partial<DemoWorkspaceState>;
      try {
        parsed = JSON.parse(event.newValue) as Partial<DemoWorkspaceState>;
      } catch {
        return;
      }
      if (!Array.isArray(parsed.patients) || !Array.isArray(parsed.encounters)) return;
      const theirs = {
        patients: (parsed.patients as DemoPatientRecord[]).map(migrateStoredPatient),
        encounters: (parsed.encounters as DemoEncounterRecord[]).map(migrateStoredEncounter),
      };
      setState((current) => mergeWorkspaceStates(current, theirs));
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [persist]);

  const clearLocalWorkspace = useCallback(() => {
    setState({ patients: [], encounters: [], results: [] });
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* Nothing stored. */
    }
  }, []);

  const followUpAppendOutcomes = state[FOLLOW_UP_APPEND_OUTCOMES];
  useEffect(() => {
    if (!followUpAppendOutcomes?.length) return;
    const handledOperationIds = new Set<string>();
    for (const outcome of followUpAppendOutcomes) {
      const resolve = followUpAppendResolversRef.current.get(outcome.operationId);
      if (!resolve) continue;
      handledOperationIds.add(outcome.operationId);
      followUpAppendResolversRef.current.delete(outcome.operationId);
      resolve(outcome.result);
    }
    if (handledOperationIds.size === 0) return;
    setState((current) => {
      const currentOutcomes = current[FOLLOW_UP_APPEND_OUTCOMES] ?? [];
      const remainingOutcomes = currentOutcomes.filter(
        (outcome) => !handledOperationIds.has(outcome.operationId),
      );
      if (remainingOutcomes.length === currentOutcomes.length) return current;
      const workspaceState: DemoWorkspaceState = {
        patients: current.patients,
        encounters: current.encounters,
        results: current.results,
      };
      return remainingOutcomes.length > 0
        ? { ...workspaceState, [FOLLOW_UP_APPEND_OUTCOMES]: remainingOutcomes }
        : workspaceState;
    });
  }, [followUpAppendOutcomes]);

  const addPatient = useCallback((input: DemoPatientInput): DemoPatientRecord => {
    const patient = normalizedPatient(input);
    setState((current) => ({ ...current, patients: [...current.patients, patient] }));
    return patient;
  }, []);

  const updatePatient = useCallback(
    (patientId: string, input: DemoPatientInput): DemoPatientRecord => {
      const existing = state.patients.find((patient) => patient.id === patientId);
      if (!existing) throw new Error("Synthetic patient was not found");
      const result = normalizedPatient(input, existing);
      setState((current) => {
        return {
          ...current,
          patients: current.patients.map((patient) =>
            patient.id === patientId ? result : patient,
          ),
        };
      });
      return result;
    },
    [state.patients],
  );

  const startEncounter = useCallback(
    (patientId: string, chiefComplaint: string): DemoEncounterRecord => {
      const startedAt = now();
      const encounter: DemoEncounterRecord = {
        id: newId("demo-encounter"),
        patientId,
        chiefComplaint: chiefComplaint.trim(),
        startedAt,
        updatedAt: startedAt,
        draftSavedAt: null,
        note: { subjective: "", objective: "", assessment: "", plan: "" },
      };
      setState((current) => ({ ...current, encounters: [encounter, ...current.encounters] }));
      return encounter;
    },
    [],
  );

  const updateEncounterNote = useCallback(
    (
      encounterId: string,
      note: EncounterSoapNote | ((current: EncounterSoapNote) => EncounterSoapNote),
      source?: "clinician",
    ): void => {
      setState((current) => ({
        ...current,
        encounters: current.encounters.map((encounter) => {
          if (encounter.id !== encounterId) return encounter;
          // The updater form reads the note inside the state transition, so
          // rapid sequential writes (e.g. a batch appending plan lines) can
          // never overwrite each other from stale render closures. It must be
          // pure; returning the identical note object skips the update.
          const nextNote = typeof note === "function" ? note(encounter.note) : note;
          if (nextNote === encounter.note) return encounter;
          const diagnosisInputsChanged =
            nextNote.subjective !== encounter.note.subjective ||
            nextNote.objective !== encounter.note.objective ||
            nextNote.assessment !== encounter.note.assessment;
          return {
            ...encounter,
            note: nextNote,
            clinicianIntent: {
              ...encounterClinicianIntent(encounter),
              ...(source === "clinician" && nextNote.assessment !== encounter.note.assessment
                ? { assessment: nextNote.assessment }
                : {}),
              ...(source === "clinician" && nextNote.plan !== encounter.note.plan
                ? { plan: nextNote.plan }
                : {}),
            },
            diagnosisCode: diagnosisInputsChanged ? undefined : encounter.diagnosisCode,
            diagnosisSource: diagnosisInputsChanged ? undefined : encounter.diagnosisSource,
            updatedAt: now(),
            draftSavedAt: null,
          };
        }),
      }));
    },
    [],
  );

  const appendEncounterFollowUps = useCallback(
    (
      encounterId: string,
      additions: DemoFollowUpNoteAdditions,
      records: readonly DemoFollowUpDocumentationRecord[],
    ): Promise<DemoFollowUpAppendResult> => {
      if (records.length === 0) {
        return Promise.resolve({ status: "rejected", reason: "empty_batch" });
      }
      const operationId = newId("follow-up-append");
      return new Promise((resolve) => {
        followUpAppendResolversRef.current.set(operationId, resolve);
        setState((current) => {
          const encounterIndex = current.encounters.findIndex(
            (encounter) => encounter.id === encounterId,
          );
          let result: DemoFollowUpAppendResult;
          let encounters = current.encounters;
          if (encounterIndex < 0) {
            result = { status: "rejected", reason: "encounter_not_found" };
          } else {
            const encounter = current.encounters[encounterIndex];
            if (!encounter) {
              result = { status: "rejected", reason: "encounter_not_found" };
            } else if (encounter.completedAt || encounter.executedActions?.length) {
              result = { status: "rejected", reason: "encounter_locked" };
            } else {
              const note = {
                ...encounter.note,
                subjective: appendClinicalProse(
                  encounter.note.subjective,
                  additions.subjective ?? [],
                ),
                objective: appendClinicalProse(encounter.note.objective, additions.objective ?? []),
              };
              // The dialog preflights this limit for inline feedback. Recheck
              // against the freshest state and reject the entire mixed batch.
              const fullSection =
                note.subjective.length > 12_000
                  ? ("subjective" as const)
                  : note.objective.length > 12_000
                    ? ("objective" as const)
                    : null;
              if (fullSection) {
                result = {
                  status: "rejected",
                  reason: "section_full",
                  section: fullSection,
                };
              } else {
                const diagnosisInputsChanged =
                  note.subjective !== encounter.note.subjective ||
                  note.objective !== encounter.note.objective ||
                  note.assessment !== encounter.note.assessment;
                const updatedEncounter: DemoEncounterRecord = {
                  ...encounter,
                  note,
                  followUpDocumentation: [...(encounter.followUpDocumentation ?? []), ...records],
                  diagnosisCode: diagnosisInputsChanged ? undefined : encounter.diagnosisCode,
                  diagnosisSource: diagnosisInputsChanged ? undefined : encounter.diagnosisSource,
                  updatedAt: now(),
                  draftSavedAt: null,
                };
                encounters = current.encounters.map((candidate, index) =>
                  index === encounterIndex ? updatedEncounter : candidate,
                );
                result = { status: "accepted" };
              }
            }
          }
          return {
            ...current,
            encounters,
            [FOLLOW_UP_APPEND_OUTCOMES]: [
              ...(current[FOLLOW_UP_APPEND_OUTCOMES] ?? []),
              { operationId, result },
            ],
          };
        });
      });
    },
    [],
  );

  const updateEncounterWorkflow = useCallback(
    (encounterId: string, patch: DemoEncounterWorkflowPatch): void => {
      setState((current) => ({
        ...current,
        encounters: current.encounters.map((encounter) => {
          if (encounter.id !== encounterId) return encounter;
          return {
            ...encounter,
            updatedAt: now(),
            ...(patch.clinicianIntent ? { clinicianIntent: patch.clinicianIntent } : {}),
            ...(patch.encounterProblems !== undefined
              ? { encounterProblems: patch.encounterProblems, draftSavedAt: null }
              : {}),
            ...(patch.problemSelections !== undefined
              ? { problemSelections: patch.problemSelections, draftSavedAt: null }
              : {}),
            ...(patch.problemDiagnoses !== undefined
              ? { problemDiagnoses: patch.problemDiagnoses, draftSavedAt: null }
              : {}),
            ...("executedActions" in patch
              ? { executedActions: patch.executedActions ?? undefined }
              : {}),
            ...("discardBaseline" in patch
              ? { discardBaseline: patch.discardBaseline ?? undefined }
              : {}),
            ...("completedAt" in patch ? { completedAt: patch.completedAt ?? null } : {}),
            ...("completedBy" in patch ? { completedBy: patch.completedBy ?? null } : {}),
            ...("diagnosisCode" in patch
              ? {
                  diagnosisCode: patch.diagnosisCode ?? undefined,
                  diagnosisSource: patch.diagnosisCode
                    ? (patch.diagnosisSource ?? UNDECLARED_DIAGNOSIS_SOURCE)
                    : undefined,
                  draftSavedAt: null,
                }
              : {}),
          };
        }),
      }));
    },
    [],
  );

  const recordLabResults = useCallback((records: readonly DemoLabResultRecord[]): void => {
    if (records.length === 0) return;
    // Last write wins by id, both against existing state and within the batch.
    const incoming = [...new Map(records.map((record) => [record.id, record])).values()];
    setState((current) => {
      const replacedIds = new Set(incoming.map((record) => record.id));
      return {
        ...current,
        results: [...current.results.filter((record) => !replacedIds.has(record.id)), ...incoming],
      };
    });
  }, []);

  const removeEncounterResults = useCallback((encounterId: string): void => {
    setState((current) => ({
      ...current,
      results: current.results.filter((record) => record.encounterId !== encounterId),
    }));
  }, []);

  const acknowledgeLabResult = useCallback((resultId: string, acknowledgedBy: string): void => {
    setState((current) => ({
      ...current,
      results: current.results.map((record) =>
        record.id === resultId ? { ...record, acknowledgedAt: now(), acknowledgedBy } : record,
      ),
    }));
  }, []);

  const patientFor = useCallback(
    (patientId: string) => state.patients.find((patient) => patient.id === patientId),
    [state.patients],
  );
  const encounterFor = useCallback(
    (encounterId: string) => state.encounters.find((encounter) => encounter.id === encounterId),
    [state.encounters],
  );
  const snapshotFor = useCallback(
    (encounterId: string): EncounterSnapshot | undefined => {
      const encounter = state.encounters.find((candidate) => candidate.id === encounterId);
      if (!encounter) return undefined;
      const patient = state.patients.find((candidate) => candidate.id === encounter.patientId);
      if (!patient) return undefined;
      return {
        patient: {
          id: patient.id,
          displayName: patient.displayName,
          dob: patient.dob,
          sex: patient.sex,
          mrn: patient.mrn,
          healthNumber: patient.healthNumber,
          healthVersionCode: patient.healthVersionCode,
          phone: patient.phone,
          address: patient.address,
          allergies: patient.allergies,
          medications: patient.medications,
          problems: patient.problems,
        },
        patientAgeYears: patientAge(patient.dob),
        encounterId: encounter.id,
        chiefComplaint: encounter.chiefComplaint,
        note: encounter.note,
        clinicianIntent: encounterClinicianIntent(encounter),
        ...(encounter.encounterProblems ? { problems: [...encounter.encounterProblems] } : {}),
        ...(encounter.problemSelections
          ? { problemSelections: [...encounter.problemSelections] }
          : {}),
      };
    },
    [state.encounters, state.patients],
  );

  const saveEncounterDraft = useCallback(
    (encounterId: string): EncounterSnapshot => {
      const snapshot = snapshotFor(encounterId);
      if (!snapshot) throw new Error("Synthetic encounter was not found");
      const savedAt = now();
      setState((current) => ({
        ...current,
        encounters: current.encounters.map((encounter) =>
          encounter.id === encounterId
            ? { ...encounter, draftSavedAt: savedAt, updatedAt: savedAt }
            : encounter,
        ),
      }));
      return snapshot;
    },
    [snapshotFor],
  );

  const ensureScheduledEncounter = useCallback(
    (
      appointment: DemoAppointment,
    ): { patient: DemoPatientRecord; encounter: DemoEncounterRecord } => {
      const existingEncounter = state.encounters.find(
        (encounter) =>
          encounter.schedulingAppointmentId === appointment.id ||
          encounter.id === appointment.encounterId,
      );
      if (existingEncounter !== undefined) {
        const patient = state.patients.find(
          (candidate) => candidate.id === existingEncounter.patientId,
        );
        if (patient !== undefined) return { patient, encounter: existingEncounter };
      }

      const matchedPatient = state.patients.find(
        (candidate) =>
          candidate.id === appointment.patient.patientId ||
          (candidate.displayName.localeCompare(appointment.patient.displayName, undefined, {
            sensitivity: "base",
          }) === 0 &&
            candidate.dob === appointment.patient.dateOfBirth),
      );
      const patient =
        matchedPatient ??
        normalizedPatient({
          displayName: appointment.patient.displayName,
          dob: appointment.patient.dateOfBirth,
          sex: demoChartSexForBooking(appointment.patient.sex),
          healthNumber: "",
          healthVersionCode: "",
          phone: appointment.patient.phone,
          address: "Synthetic appointment intake — address to confirm at check-in",
          allergies: [],
          medications: [],
          problems: [],
        });
      const startedAt = now();
      const encounter: DemoEncounterRecord = {
        id: newId("demo-encounter"),
        patientId: patient.id,
        chiefComplaint: appointment.reason,
        startedAt,
        updatedAt: startedAt,
        draftSavedAt: null,
        schedulingAppointmentId: appointment.id,
        note: { subjective: "", objective: "", assessment: "", plan: "" },
      };
      setState((current) => ({
        ...current,
        patients: current.patients.some((candidate) => candidate.id === patient.id)
          ? current.patients
          : [...current.patients, patient],
        encounters: [encounter, ...current.encounters],
      }));
      return { patient, encounter };
    },
    [state.encounters, state.patients],
  );

  const ensureExternalEncounter = useCallback(
    (
      input: DemoExternalEncounterInput,
    ): { patient: DemoPatientRecord; encounter: DemoEncounterRecord } => {
      const changedAt = now();
      const displayName = input.displayName.trim() || "Unnamed patient";
      const allergies = normalizedList(input.allergies ?? []);
      const medications = normalizedList(input.medications ?? []);
      const problems = normalizedList(input.problems ?? []);

      const existingPatient = state.patients.find((candidate) => candidate.id === input.patientId);
      const healthNumber = input.healthNumber?.trim();
      const patientChanged =
        existingPatient !== undefined &&
        (existingPatient.displayName !== displayName ||
          existingPatient.dob !== input.dob ||
          (input.mrn !== undefined && existingPatient.mrn !== input.mrn) ||
          (healthNumber !== undefined && existingPatient.healthNumber !== healthNumber) ||
          JSON.stringify(existingPatient.allergies) !== JSON.stringify(allergies) ||
          JSON.stringify(existingPatient.medications) !== JSON.stringify(medications) ||
          JSON.stringify(existingPatient.problems) !== JSON.stringify(problems));
      const patient: DemoPatientRecord =
        existingPatient === undefined
          ? {
              id: input.patientId,
              displayName,
              dob: input.dob,
              sex: input.sex ?? "female",
              mrn: input.mrn?.trim() || `EXT-${input.patientId.slice(0, 8).toUpperCase()}`,
              healthNumber: healthNumber ?? "",
              healthVersionCode: "",
              phone: input.phone?.trim() || "On persisted file",
              address:
                input.address?.trim() ||
                "Persisted record — demographics held in the authoritative chart",
              allergies,
              medications,
              problems,
              createdAt: changedAt,
              updatedAt: changedAt,
            }
          : patientChanged
            ? {
                ...existingPatient,
                displayName,
                dob: input.dob,
                ...(input.mrn === undefined ? {} : { mrn: input.mrn }),
                ...(healthNumber === undefined ? {} : { healthNumber }),
                allergies,
                medications,
                problems,
                updatedAt: changedAt,
              }
            : existingPatient;

      const existingEncounter = state.encounters.find(
        (candidate) => candidate.id === input.encounterId,
      );
      let encounter: DemoEncounterRecord;
      if (existingEncounter === undefined) {
        encounter = {
          id: input.encounterId,
          patientId: input.patientId,
          chiefComplaint: input.chiefComplaint.trim(),
          startedAt: changedAt,
          updatedAt: changedAt,
          draftSavedAt: null,
          note: input.note ?? { subjective: "", objective: "", assessment: "", plan: "" },
          ...(input.encounterProblems !== undefined
            ? { encounterProblems: input.encounterProblems }
            : {}),
          ...(input.problemSelections !== undefined
            ? { problemSelections: input.problemSelections }
            : {}),
          ...(input.problemDiagnoses !== undefined
            ? { problemDiagnoses: input.problemDiagnoses }
            : {}),
          ...(input.diagnosisCode
            ? {
                diagnosisCode: input.diagnosisCode,
                diagnosisSource: input.diagnosisSource ?? UNDECLARED_DIAGNOSIS_SOURCE,
              }
            : {}),
          ...(input.completedAt ? { completedAt: input.completedAt } : {}),
          ...(input.completedAt && input.completedBy ? { completedBy: input.completedBy } : {}),
        };
      } else {
        // A record whose updatedAt still equals startedAt has never been
        // edited locally, so the persisted note may refresh it. Local edits
        // always win over re-hydration.
        const neverEditedLocally = existingEncounter.updatedAt === existingEncounter.startedAt;
        const noteDiffers =
          input.note !== undefined &&
          (existingEncounter.note.subjective !== input.note.subjective ||
            existingEncounter.note.objective !== input.note.objective ||
            existingEncounter.note.assessment !== input.note.assessment ||
            existingEncounter.note.plan !== input.note.plan);
        const adoptNote = input.note !== undefined && neverEditedLocally && noteDiffers;
        // The documented diagnosis code follows the same rule: the persisted
        // set refreshes a chip this browser never touched and nothing else.
        const adoptDiagnosis =
          input.diagnosisCode !== undefined &&
          neverEditedLocally &&
          (!sameDiagnosisCode(existingEncounter.diagnosisCode, input.diagnosisCode) ||
            (input.diagnosisCode !== null &&
              existingEncounter.diagnosisSource !==
                (input.diagnosisSource ?? UNDECLARED_DIAGNOSIS_SOURCE)));
        const adoptCompletion =
          typeof input.completedAt === "string" && !existingEncounter.completedAt;
        const adoptProblems =
          neverEditedLocally &&
          ((input.encounterProblems !== undefined &&
            JSON.stringify(input.encounterProblems) !==
              JSON.stringify(existingEncounter.encounterProblems)) ||
            (input.problemSelections !== undefined &&
              JSON.stringify(input.problemSelections) !==
                JSON.stringify(existingEncounter.problemSelections)) ||
            (input.problemDiagnoses !== undefined &&
              JSON.stringify(input.problemDiagnoses) !==
                JSON.stringify(existingEncounter.problemDiagnoses)));
        encounter =
          adoptNote || adoptDiagnosis || adoptCompletion || adoptProblems
            ? {
                ...existingEncounter,
                ...(adoptProblems && input.encounterProblems !== undefined
                  ? { encounterProblems: input.encounterProblems }
                  : {}),
                ...(adoptProblems && input.problemSelections !== undefined
                  ? { problemSelections: input.problemSelections }
                  : {}),
                ...(adoptProblems && input.problemDiagnoses !== undefined
                  ? { problemDiagnoses: input.problemDiagnoses }
                  : {}),
                ...(adoptNote && input.note !== undefined
                  ? {
                      note: input.note,
                      clinicianIntent: { assessment: input.note.assessment, plan: input.note.plan },
                    }
                  : {}),
                ...(adoptDiagnosis
                  ? {
                      diagnosisCode: input.diagnosisCode ?? undefined,
                      diagnosisSource: input.diagnosisCode
                        ? (input.diagnosisSource ?? UNDECLARED_DIAGNOSIS_SOURCE)
                        : undefined,
                    }
                  : {}),
                ...(adoptCompletion && typeof input.completedAt === "string"
                  ? {
                      completedAt: input.completedAt,
                      completedBy: input.completedBy ?? existingEncounter.completedBy ?? null,
                    }
                  : {}),
              }
            : existingEncounter;
      }

      if (patient !== existingPatient || encounter !== existingEncounter) {
        setState((current) => ({
          ...current,
          patients:
            existingPatient === undefined
              ? [...current.patients, patient]
              : patient === existingPatient
                ? current.patients
                : current.patients.map((candidate) =>
                    candidate.id === patient.id ? patient : candidate,
                  ),
          encounters:
            existingEncounter === undefined
              ? [encounter, ...current.encounters]
              : encounter === existingEncounter
                ? current.encounters
                : current.encounters.map((candidate) =>
                    candidate.id === encounter.id ? encounter : candidate,
                  ),
        }));
      }
      return { patient, encounter };
    },
    [state.encounters, state.patients],
  );

  const value = useMemo<DemoWorkspaceContextValue>(
    () => ({
      ...state,
      addPatient,
      updatePatient,
      startEncounter,
      updateEncounterNote,
      appendEncounterFollowUps,
      updateEncounterWorkflow,
      recordLabResults,
      removeEncounterResults,
      acknowledgeLabResult,
      saveEncounterDraft,
      patientFor,
      encounterFor,
      ensureScheduledEncounter,
      ensureExternalEncounter,
      snapshotFor,
      clearLocalWorkspace,
    }),
    [
      acknowledgeLabResult,
      clearLocalWorkspace,
      addPatient,
      appendEncounterFollowUps,
      encounterFor,
      ensureScheduledEncounter,
      ensureExternalEncounter,
      patientFor,
      recordLabResults,
      removeEncounterResults,
      saveEncounterDraft,
      snapshotFor,
      startEncounter,
      state,
      updateEncounterNote,
      updateEncounterWorkflow,
      updatePatient,
    ],
  );

  return <DemoWorkspaceContext.Provider value={value}>{children}</DemoWorkspaceContext.Provider>;
}

export function useDemoWorkspace(): DemoWorkspaceContextValue {
  const context = useContext(DemoWorkspaceContext);
  if (!context) throw new Error("useDemoWorkspace must be used inside DemoWorkspaceProvider");
  return context;
}

export function patientInitials(patient: Pick<DemoPatientRecord, "displayName">): string {
  return splitSeedName(patient.displayName);
}

export function patientAge(dob: string): number {
  const birth = new Date(`${dob}T00:00:00`);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const beforeBirthday =
    today.getMonth() < birth.getMonth() ||
    (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate());
  if (beforeBirthday) age -= 1;
  return Math.max(0, age);
}
